import React, { useEffect, useMemo, useState } from "react";
import DoctorHeader from "./DoctorHeader";
import DoctorSidebar, { DoctorSidebarDrawer } from "./DoctorSidebar";
import DoctorFooter from "./DoctorFooter";
import { CircularProgress, IconButton, Button } from "@mui/material";
import {
  Menu,
  RefreshCw,
  UserRound,
  Stethoscope,
  Pill,
  Wallet,
  CheckCircle2,
  Clock3,
  ClipboardList,
} from "lucide-react";
import axios from "axios";
import SimpleSnackbar from "../Snackbar";

const AdminRequests = () => {
  const [requests, setRequests] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loadingId, setLoadingId] = useState(null);

  const [snackMessage, setSnackMessage] = useState("");
  const [snackColor, setSnackColor] = useState("success");
  const [loading, setLoading] = useState(false);

  const fetchRequests = async () => {
    try {
      setLoading(true);

      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/prescriptions`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (res.data.success) {
        setRequests(res.data.requests || []);
      }
    } catch (error) {
      console.error(error);
      setSnackMessage("Failed to load requests");
      setSnackColor("error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const closeRequest = async (id) => {
    try {
      setLoadingId(id);
      setSnackMessage("Closing request...");
      setSnackColor("info");

      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/prescriptions/${id}/close`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      setSnackMessage("Request closed successfully");
      setSnackColor("success");

      fetchRequests();
    } catch (error) {
      console.error(error);
      setSnackMessage("Failed to close request");
      setSnackColor("error");
    } finally {
      setLoadingId(null);
    }
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case "paid":
        return "bg-emerald-50 text-emerald-700";
      case "closed":
        return "bg-slate-100 text-slate-600";
      case "costed":
        return "bg-blue-50 text-blue-700";
      case "pending":
      default:
        return "bg-amber-50 text-amber-700";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "paid":
        return "Paid";
      case "closed":
        return "Closed";
      case "costed":
        return "Costed";
      case "pending":
      default:
        return "Pending";
    }
  };

  const stats = useMemo(() => {
    const total = requests.length;
    const pending = requests.filter((r) => r.status === "pending").length;
    const costed = requests.filter((r) => r.status === "costed").length;
    const paid = requests.filter((r) => r.status === "paid").length;
    const closed = requests.filter((r) => r.status === "closed").length;

    const totalValue = requests.reduce(
      (sum, r) => sum + Number(r.totalCost || 0),
      0
    );

    return {
      total,
      pending,
      costed,
      paid,
      closed,
      totalValue,
    };
  }, [requests]);

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <DoctorHeader />

      {/* Mobile Header */}
      <div className="flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3 md:hidden">
        <div>
          <p className="text-xs text-slate-400">Admin</p>
          <h1 className="text-base font-semibold text-slate-950">
            Prescription Requests
          </h1>
        </div>

        <IconButton onClick={() => setDrawerOpen(true)}>
          <Menu />
        </IconButton>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <div className="hidden lg:flex">
          <DoctorSidebar activeTab="Requests" setActiveTab={() => {}} />
        </div>

        <DoctorSidebarDrawer
          open={drawerOpen}
          onOpen={() => setDrawerOpen(true)}
          onClose={() => setDrawerOpen(false)}
          activeTab="Requests"
          setActiveTab={() => setDrawerOpen(false)}
        />

        {/* Main */}
        <main className="flex-1 overflow-y-auto">
          <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
            {/* Page Title */}
            <div className="mb-8 flex flex-col gap-5">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <h2 className="text-2xl font-semibold tracking-[-0.03em] text-slate-950 sm:text-3xl">
                    Prescription Requests
                  </h2>

                  <p className="mt-2 text-sm text-slate-500">
                    Track pharmacy costing, patient payments, and completed
                    requests.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={fetchRequests}
                  className="inline-flex h-11 w-fit items-center gap-2 rounded-xl bg-slate-900 px-4 text-sm font-medium text-white transition hover:bg-slate-800"
                >
                  {loading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <RefreshCw size={16} />
                  )}
                  Refresh
                </button>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
                <StatCard
                  title="Total requests"
                  value={stats.total.toLocaleString()}
                  icon={ClipboardList}
                />

                <StatCard
                  title="Paid"
                  value={stats.paid.toLocaleString()}
                  icon={CheckCircle2}
                />

                <StatCard
                  title="Pending"
                  value={stats.pending.toLocaleString()}
                  icon={Clock3}
                />

                <StatCard
                  title="Closed"
                  value={stats.closed.toLocaleString()}
                  icon={CheckCircle2}
                />

                <StatCard
                  title="Total value"
                  value={`₦${stats.totalValue.toLocaleString()}`}
                  icon={Wallet}
                />
              </div>
            </div>

            {/* List Header */}
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-base font-semibold text-slate-950">
                Requests
              </h3>

              <p className="text-sm text-slate-500">
                {requests.length.toLocaleString()} request
                {requests.length === 1 ? "" : "s"}
              </p>
            </div>

            {/* Empty */}
            {!loading && requests.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-14 text-center">
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
                  <Pill size={20} className="text-slate-500" />
                </div>

                <h3 className="text-base font-semibold text-slate-950">
                  No prescription requests
                </h3>

                <p className="mx-auto mt-2 max-w-sm text-sm leading-6 text-slate-500">
                  Pharmacy costing and prescription payment requests will appear
                  here.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                {requests.map((request) => (
                  <RequestCard
                    key={request._id}
                    request={request}
                    loadingId={loadingId}
                    closeRequest={closeRequest}
                    getStatusStyle={getStatusStyle}
                    getStatusLabel={getStatusLabel}
                  />
                ))}
              </div>
            )}
          </div>
        </main>
      </div>


      <SimpleSnackbar
        message={snackMessage}
        color={snackColor}
        onClose={() => setSnackMessage("")}
      />
    </div>
  );
};

const StatCard = ({ title, value, icon: Icon }) => {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm text-slate-500">{title}</p>
          <p className="mt-2 text-2xl font-semibold tracking-[-0.03em] text-slate-950">
            {value}
          </p>
        </div>

        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-600">
          <Icon size={18} />
        </div>
      </div>
    </div>
  );
};

const RequestCard = ({
  request,
  loadingId,
  closeRequest,
  getStatusStyle,
  getStatusLabel,
}) => {
  const patientName = request.patientId?.name || "Patient";
  const doctorName = request.doctorId?.name || "—";
  const totalCost = Number(request.totalCost || 0);

  return (
    <article className="rounded-2xl border border-slate-200 bg-white p-5 transition hover:border-slate-300 hover:shadow-sm">
      {/* Top */}
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-slate-900 text-sm font-semibold text-white">
              {patientName
                .split(" ")
                .filter(Boolean)
                .map((item) => item[0])
                .join("")
                .slice(0, 2)
                .toUpperCase() || "PT"}
            </div>

            <div className="min-w-0">
              <h4 className="truncate text-sm font-semibold text-slate-950">
                {patientName}
              </h4>

              <p className="mt-1 flex items-center gap-1 text-xs text-slate-500">
                <Stethoscope size={13} />
                <span className="truncate">Dr. {doctorName}</span>
              </p>
            </div>
          </div>
        </div>

        <span
          className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-medium ${getStatusStyle(
            request.status
          )}`}
        >
          {getStatusLabel(request.status)}
        </span>
      </div>

      {/* Drugs */}
      <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-4">
        <p className="mb-3 text-xs font-medium uppercase tracking-wide text-slate-400">
          Drugs
        </p>

        <div className="space-y-3">
          {(request.drugs || []).map((drug, index) => (
            <div
              key={index}
              className="flex items-start justify-between gap-4 border-b border-slate-200 pb-3 last:border-b-0 last:pb-0"
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-slate-800">
                  {drug.name || "Unnamed drug"}
                </p>

                <p className="mt-1 text-xs text-slate-500">
                  Qty: {drug.quantity || "—"}
                </p>
              </div>

              <p className="shrink-0 text-sm font-semibold text-slate-950">
                ₦{Number(drug.price || 0).toLocaleString()}
              </p>
            </div>
          ))}

          {!request.drugs?.length && (
            <p className="text-sm text-slate-500">No drugs attached.</p>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="mt-5 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs text-slate-400">Total cost</p>
          <p className="mt-1 text-xl font-semibold text-slate-950">
            ₦{totalCost.toLocaleString()}
          </p>
        </div>

        {request.status === "paid" && (
          <Button
            onClick={() => closeRequest(request._id)}
            disabled={loadingId === request._id}
            sx={{
              minHeight: 42,
              bgcolor: "#075E54",
              color: "white",
              borderRadius: "12px",
              textTransform: "none",
              fontWeight: 600,
              boxShadow: "none",
              px: 3,
              "&:hover": {
                bgcolor: "#064b43",
                boxShadow: "none",
              },
              "&:disabled": {
                bgcolor: "#e2e8f0",
                color: "#64748b",
              },
            }}
          >
            {loadingId === request._id ? "Processing..." : "Mark as closed"}
          </Button>
        )}

        {request.status === "closed" && (
          <span className="inline-flex items-center gap-2 rounded-xl bg-slate-100 px-3 py-2 text-sm font-medium text-slate-600">
            <CheckCircle2 size={16} />
            Completed
          </span>
        )}
      </div>
    </article>
  );
};

export default AdminRequests;
